import React, { useState, useMemo } from "react";
import { Card, CardBody, Col, Container, Row, Button, Input, Label, Spinner, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { DataTable, Column, TableAction } from "../../../Components/Common/DataTable";
import { fakeDegradations } from "../../../utils/fakeData";
import { showConfirmModal, showModal } from "../../../hooks/useModal";
import { useAlerteDetailQueries, useValiderAlerteMutation, useDeleteAlerteMutation } from "../../../hooks/queries/useRoadAlertQueries";
import { toastSuccess, toastError } from "../../../utils/toast";
import AffectationEquipeModal from "../AlertesList/AffectationEquipeModal";

const AlerteDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("1");

  const { alerte, degradations: degradationsRaw, documents, loading: detailLoading, refetch } = useAlerteDetailQueries(id);
  const validerMutation = useValiderAlerteMutation();
  const deleteMutation = useDeleteAlerteMutation();
  const navigate = useNavigate();

  const degradations = degradationsRaw.length > 0 ? degradationsRaw : fakeDegradations;
  const loading = detailLoading;

  // Configuration des colonnes pour les dégradations (doit être avant le return conditionnel)
  const degradationsColumns: Column[] = useMemo(() => [
    {
      key: "libelleDegradation",
      label: "Libellé",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: any) => (
        <span className="text-gray-900">
          {row?.degradation?.libelleDegradation || row?.libelleDegradation || "-"}
        </span>
      ),
    },
  ], []);

  // Actions dynamiques pour les dégradations
  const degradationsActions: TableAction<any>[] = useMemo(() => [
    {
      label: "Voir les détails",
      icon: "ri-eye-line",
      onClick: (row) => {
        console.log("Voir détails dégradation:", row);
        // TODO: Implémenter la navigation vers les détails de la dégradation
      },
      variant: "primary",
    },
    {
      label: "Modifier",
      icon: "ri-edit-line",
      onClick: (row) => {
        console.log("Modifier dégradation:", row);
        // TODO: Implémenter la modification
      },
      variant: "info",
    },
    {
      label: "Supprimer",
      icon: "ri-delete-bin-line",
      onClick: (row) => {
        const libelle = row?.degradation?.libelleDegradation || row?.libelleDegradation || "cette dégradation";
        showConfirmModal({
          title: "Supprimer la dégradation",
          message: `Êtes-vous sûr de vouloir supprimer ${libelle} ? Cette action est irréversible.`,
          confirmText: "Oui, supprimer",
          cancelText: "Annuler",
          confirmVariant: "danger",
          type: "delete",
          onConfirm: () => {
            console.log("Supprimer dégradation:", row);
            // TODO: Implémenter la suppression
          },
        });
      },
      variant: "danger",
    },
  ], []);

  document.title = alerte ? `Alerte ${alerte.libelleAlerte} | Road Alert` : "Détail alerte | Road Alert";

  if (loading || !alerte) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  const lat = alerte.latitude != null ? Number(alerte.latitude) : null;
  const lon = alerte.longitude != null ? Number(alerte.longitude) : null;
  const hasMap = lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon);
  // Bouton en haut : ouvrir Google Maps
  const mapLinkUrl = hasMap ? `https://www.google.com/maps?q=${lat},${lon}` : null;
  // Onglet MAP : carte OpenStreetMap en iframe
  const mapEmbedUrl = hasMap
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.01},${lat - 0.01},${lon + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lon}`
    : null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Consultation d'une alerte" pageTitle="Alertes" />
          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
            <CardBody>
              <h5 className="card-title mb-4 fw-semibold">Détails de l'alerte</h5>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Libellé</Label>
                    <Input type="text" value={alerte.libelleAlerte || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Status alerte</Label>
                    <Input type="text" value={alerte.statutAlerte?.libelleStatutAlerte || alerte.libelleStatutAlerte || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Priorité alerte</Label>
                    <Input type="text" value={alerte.priorite?.libellePriorite || alerte.libellePriorite || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Type travaux</Label>
                    <Input type="text" value={alerte.typeTravaux?.libelleTypeTravaux || alerte.libelleTypeTravaux || ""} readOnly />
                  </div>
                </Col>
                <Col md={12}>
                  <div className="mb-3">
                    <Label>Description</Label>
                    <Input type="textarea" rows={2} value={alerte.description || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Nom Voie</Label>
                    <Input type="text" value={alerte.nomVoie || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Type Chaussée</Label>
                    <Input type="text" value={alerte.typeChausse?.libelleTypeChausse || alerte.libelleTypeChausse || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Quartier</Label>
                    <Input type="text" value={alerte.quartier || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Commune</Label>
                    <Input type="text" value={alerte.commune?.libelleCommune || alerte.libelleCommune || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Longitude</Label>
                    <Input type="text" value={alerte.longitude || ""} readOnly />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Latitude</Label>
                    <Input type="text" value={alerte.latitude || ""} readOnly />
                  </div>
                </Col>
                {mapLinkUrl && (
                  <Col md={12}>
                    <a
                      href={mapLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn text-white"
                      style={{ backgroundColor: '#311B91', borderColor: '#311B91' }}
                    >
                      <i className="ri-map-pin-line me-1"></i> Carte (Google Maps)
                    </a>
                  </Col>
                )}
                <Col md={12} className="mt-3 d-flex gap-2 flex-wrap">
                  {alerte.statutAlerte?.idStatutAlerte === 1 && (
                    <Button
                      color="success"
                      onClick={() => {
                        showModal({
                          title: "Affecter l'alerte à une équipe",
                          size: "md",
                          content: (
                            <AffectationEquipeModal
                              alerte={alerte}
                              onSuccess={() => {
                                refetch();
                              }}
                            />
                          ),
                          closeOnBackdropClick: false,
                          haveDefaultActionsButton: false,
                        });
                      }}
                    >
                      <i className="ri-user-add-line me-1"></i> Affecter
                    </Button>
                  )}
                  {alerte.statutAlerte?.idStatutAlerte === 2 && (
                    <Button
                      color="success"
                      disabled={validerMutation.isPending}
                      onClick={() => {
                        showConfirmModal({
                          title: "Valider l'alerte",
                          message: "Voulez-vous valider cette alerte et la passer en cours ?",
                          confirmText: "Valider",
                          cancelText: "Annuler",
                          confirmVariant: "primary",
                          type: "info",
                          onConfirm: () => {
                            validerMutation.mutate(alerte.idAlerte, {
                              onSuccess: () => {
                                toastSuccess("L'alerte a été validée.");
                                refetch();
                              },
                              onError: (err: any) => {
                                toastError(err?.message ?? "Erreur lors de la validation.");
                              },
                            });
                          },
                        });
                      }}
                    >
                      <i className="ri-checkbox-circle-line me-1"></i>
                      {validerMutation.isPending ? "Validation…" : "Valider"}
                    </Button>
                  )}
                  {(alerte.statutAlerte?.idStatutAlerte === 1 || alerte.statutAlerte?.idStatutAlerte === 7) && (
                    <Button
                      color="danger"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        showConfirmModal({
                          title: "Supprimer l'alerte",
                          message: `Êtes-vous sûr de vouloir supprimer l'alerte « ${alerte.libelleAlerte ?? alerte.description ?? `#${alerte.idAlerte}`} » ? Cette action est irréversible.`,
                          confirmText: "Oui, supprimer",
                          cancelText: "Annuler",
                          confirmVariant: "danger",
                          type: "delete",
                          onConfirm: () => {
                            deleteMutation.mutate(alerte.idAlerte, {
                              onSuccess: () => {
                                toastSuccess("L'alerte a été supprimée avec succès.");
                                navigate(-1); // Retour à la liste
                              },
                              onError: (err: any) => {
                                toastError(err?.message ?? "Erreur lors de la suppression.");
                              },
                            });
                          },
                        });
                      }}
                    >
                      <i className="ri-delete-bin-line me-1"></i>
                      {deleteMutation.isPending ? "Suppression…" : "Supprimer"}
                    </Button>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
            <CardBody>
              <Nav tabs className="modern-tabs">
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")}>
                    Dégradations
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")}>
                    Photos
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "3" })} onClick={() => setActiveTab("3")}>
                    MAP
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab} className="mt-3">
                <TabPane tabId="1">
                  <DataTable
                    instanceId={`degradations-${id}`}
                    columns={degradationsColumns}
                    data={degradations}
                    isLoading={false}
                    exportExcel={false}
                    exportPDF={false}
                    actions={degradationsActions}
                    actionsColumnLabel="Actions"
                    emptyState={
                      <div className="text-center py-12">
                        <div className="text-gray-400 mb-4">
                          <i className="ri-file-list-line" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h3 className="text-lg fw-semibold text-gray-900 mb-2">
                          Aucune dégradation
                        </h3>
                        <p className="text-gray-500">
                          Aucune dégradation n'a été enregistrée pour cette alerte.
                        </p>
                      </div>
                    }
                    pageSize={10}
                    searchable={true}
                    pagination={degradations.length > 10}
                  />
                </TabPane>
                <TabPane tabId="2">
                  <Row>
                    {documents.map((d: any) => (
                      <Col key={d.id} md={3}>
                        {d.url || d.fileBase64 ? (
                          <img src={d.url || d.fileBase64} alt="" className="img-fluid rounded" />
                        ) : (
                          <div className="border rounded p-3 text-center text-muted">Document</div>
                        )}
                      </Col>
                    ))}
                    {documents.length === 0 && <p className="text-muted">Aucune photo</p>}
                  </Row>
                </TabPane>
                <TabPane tabId="3">
                  {mapEmbedUrl ? (
                    <iframe
                      src={mapEmbedUrl}
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      loading="lazy"
                      title="Localisation (OpenStreetMap)"
                    />
                  ) : (
                    <p className="text-muted">Coordonnées non disponibles</p>
                  )}
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AlerteDetail;
